import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { type Employee } from '@avanta/shared/types/employees';
import { cn } from '@avanta/shared/utils/cn';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddEmployeeForm } from 'components/AddEmployeeForm';
import {
  AppointmentsReservePayload,
  type AppointmentsReserveFormData,
  type StepProps,
} from 'pages/AppointmentsReserve/types';
import { StepSection } from '../../ui/StepSection';
import { type PatientCardPatient } from './components/PatientCard';
import { PatientSearchResults } from './components/PatientSearchResults';
import { SelectedPatientView } from './components/SelectedPatientView';
import { useFetchPatients } from './useFetchPatients';

export const SearchPatientStep = ({ handleNext }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { setValue, watch } = useFormContext<AppointmentsReserveFormData>();
  const [search, setSearch] = useState('');
  const [showAddNewPatient, setShowAddNewPatient] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState<PatientCardPatient>();
  const selectedPatientId = watch(AppointmentsReservePayload.patientId);
  const isPatientSelected = Boolean(selectedPatientId);

  const debouncedSearch = useDebounce(search);
  const patientSearch = !showAddNewPatient && !isPatientSelected ? debouncedSearch : '';
  const { data: patients, isLoading } = useFetchPatients(patientSearch);
  const hasSearch = Boolean(search && debouncedSearch);

  const listRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(listRef, 'element', patients);
  const showScrollShadow = hasSearch && hasOverflow && !isAtBottom;

  const handleSelectPatient = (patient: PatientCardPatient): void => {
    setSearch(patient.email);
    setSelectedPatient(patient);
    setValue(AppointmentsReservePayload.patientId, patient.id);
  };

  const handleClearPatient = (): void => {
    setSearch('');
    setSelectedPatient(undefined);
    setValue(AppointmentsReservePayload.patientId, '');
  };

  const handleAddNewPatient = (): void => {
    setShowAddNewPatient(true);
  };

  const handlePatientCreated = (employee: Employee): void => {
    handleSelectPatient(employee);
    setShowAddNewPatient(false);
  };

  if (showAddNewPatient) {
    return (
      <AddEmployeeForm
        mode="embedded"
        onSuccess={handlePatientCreated}
        buttons={{
          cancel: {
            onClick: () => setShowAddNewPatient(false),
            variant: 'terciary',
            icon: 'back',
            children: (
              <>
                <ChevronLeft className="size-6" strokeWidth={0.75} />
                {t('Common.back', { ns: LOCIZE_NS.COMMON })}
              </>
            ),
          },
          submit: {
            children: (
              <>
                {t('AppointmentsReserve.SearchPatient.next')}
                <ChevronRight className="size-6" strokeWidth={0.75} />
              </>
            ),
            icon: 'trailing',
            className: 'min-w-64 w-fit',
            variant: 'primary',
          },
        }}
      />
    );
  }

  if (isPatientSelected && selectedPatient) {
    return (
      <div className="flex w-xl flex-col gap-6">
        <SelectedPatientView
          patient={selectedPatient}
          onBack={handleClearPatient}
          onNext={handleNext}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-xl flex-1 flex-col overflow-hidden">
      <StepSection
        title={t(`AppointmentsReserve.SearchPatient.title`)}
        className="flex min-h-0 flex-1 flex-col gap-5"
        containerClassName="min-h-0 flex-1"
      >
        <Input
          value={search}
          placeholder={t(`AppointmentsReserve.SearchPatient.input.placeholder`)}
          leftIcon={Search}
          onChange={event => setSearch(event.target.value)}
          className="w-full"
        />
        {hasSearch && (
          <div
            ref={listRef}
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto scrollbar-custom"
          >
            <PatientSearchResults
              patients={patients}
              isLoading={isLoading}
              showEmptyState={!isLoading && !patients?.length}
              onPatientSelect={handleSelectPatient}
            />
          </div>
        )}
      </StepSection>
      {hasSearch && (
        <div className={cn('shrink-0 bg-white pt-6', showScrollShadow && 'shadow-mobile-menu-top')}>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            icon="leading"
            onClick={handleAddNewPatient}
          >
            <Plus className="size-6" />
            {t(`AppointmentsReserve.SearchPatient.addNewPatient`)}
          </Button>
        </div>
      )}
    </div>
  );
};
